import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { financialDataAPI } from '../services/apiServices';
import { TrendingUp, TrendingDown, Plus, X, RefreshCw, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const MutualFunds = () => {
    const [trackedFunds, setTrackedFunds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchCode, setSearchCode] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const [searching, setSearching] = useState(false);

    // Load tracked funds from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('tracked_mf_funds');
        if (saved) {
            setTrackedFunds(JSON.parse(saved));
            refreshAllFunds(JSON.parse(saved));
        }
    }, []);

    // Save to localStorage whenever trackedFunds changes
    useEffect(() => {
        if (trackedFunds.length > 0) {
            localStorage.setItem('tracked_mf_funds', JSON.stringify(trackedFunds));
        }
    }, [trackedFunds]);

    // Search for a mutual fund by scheme code
    const searchFund = async () => {
        if (!searchCode || !/^\d+$/.test(searchCode)) {
            toast.error('Please enter a valid scheme code (numbers only)');
            return;
        }

        setSearching(true);
        try {
            const data = await financialDataAPI.getMFDetails(searchCode);
            if (data) {
                setSearchResult(data);
                toast.success('Fund found!');
            } else {
                toast.error('No fund found with this code');
                setSearchResult(null);
            }
        } catch (err) {
            toast.error('Error searching for fund');
            setSearchResult(null);
        } finally {
            setSearching(false);
        }
    };

    // Add fund to tracking list
    const addFundToTracking = () => {
        if (!searchResult) return;

        const exists = trackedFunds.find(f => f.schemeCode === searchResult.schemeCode);
        if (exists) {
            toast.error('This fund is already being tracked');
            return;
        }

        setTrackedFunds(prev => [...prev, searchResult]);
        toast.success(`Added ${searchResult.schemeName}`);
        setShowAddModal(false);
        setSearchCode('');
        setSearchResult(null);
    };

    // Remove fund from tracking
    const removeFund = (schemeCode) => {
        setTrackedFunds(prev => prev.filter(f => f.schemeCode !== schemeCode));
        localStorage.setItem('tracked_mf_funds', JSON.stringify(trackedFunds.filter(f => f.schemeCode !== schemeCode)));
        toast.success('Fund removed');
    };

    // Refresh NAV for all tracked funds
    const refreshAllFunds = async (funds = trackedFunds) => {
        setLoading(true);
        try {
            const updated = await Promise.all(
                funds.map(async (fund) => {
                    try {
                        const fresh = await financialDataAPI.getMFDetails(fund.schemeCode);
                        return fresh || fund;
                    } catch {
                        return fund;
                    }
                })
            );
            setTrackedFunds(updated);
            toast.success('All funds refreshed');
        } catch (err) {
            toast.error('Error refreshing funds');
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <div className="p-4 md:p-8 max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <div className="mb-3 flex items-center gap-2 bg-purple-500/10 px-2 py-0.5 rounded-full border border-purple-500/20 w-fit">
                            <Sparkles size={10} className="text-purple-400" />
                            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-purple-400">Institutional Fund Surveillance</span>
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 uppercase tracking-tighter">
                            Mutual Funds Tracker
                        </h1>
                        <p className="text-gray-500 mt-1">Track NAV updates for your favorite mutual funds</p>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            onClick={() => refreshAllFunds()}
                            disabled={loading}
                            variant="outline"
                            className="flex items-center gap-2"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            Refresh All
                        </Button>
                        <Button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                        >
                            <Plus size={18} />
                            Add Fund
                        </Button>
                    </div>
                </div>

                {/* Fund Cards Grid */}
                {trackedFunds.length === 0 ? (
                    <Card className="p-12 text-center">
                        <p className="text-gray-400 text-lg">No funds tracked yet. Click "Add Fund" to get started!</p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {trackedFunds.map((fund) => (
                            <Card key={fund.schemeCode} className="shadow-lg hover:shadow-xl transition-shadow border-t-4 border-purple-500">
                                <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <CardTitle className="text-sm font-bold text-gray-800 line-clamp-2">
                                                {fund.schemeName}
                                            </CardTitle>
                                            <p className="text-xs text-gray-500 mt-1">Code: {fund.schemeCode}</p>
                                        </div>
                                        <button
                                            onClick={() => removeFund(fund.schemeCode)}
                                            className="p-1 hover:bg-red-50 rounded-full text-gray-400 hover:text-red-600"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {/* Current NAV */}
                                    <div className="mb-4">
                                        <p className="text-xs text-gray-400 uppercase font-bold mb-1">Current NAV</p>
                                        <h2 className="text-3xl font-black text-purple-600">
                                            ₹{fund.currentNav ? Number(fund.currentNav).toFixed(2) : '—'}
                                        </h2>
                                        <p className="text-xs text-gray-500 mt-1">as of {fund.date || 'N/A'}</p>
                                    </div>

                                    {/* Change */}
                                    {fund.change && (
                                        <div className={`flex items-center gap-2 p-2 rounded-lg ${fund.change >= 0 ? 'bg-green-50' : 'bg-red-50'
                                            }`}>
                                            {fund.change >= 0 ? (
                                                <TrendingUp size={16} className="text-green-600" />
                                            ) : (
                                                <TrendingDown size={16} className="text-red-600" />
                                            )}
                                            <div>
                                                <p className={`text-sm font-bold ${fund.change >= 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {fund.change >= 0 ? '+' : ''}₹{fund.change?.toFixed(2)}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {fund.changePercent >= 0 ? '+' : ''}{fund.changePercent?.toFixed(2)}%
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Fund House */}
                                    {fund.fundHouse && (
                                        <p className="text-xs text-gray-600 mt-3 font-medium">
                                            {fund.fundHouse}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Add Fund Modal */}
                {showAddModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <Card className="w-full max-w-lg">
                            <CardHeader className="border-b">
                                <div className="flex justify-between items-center">
                                    <CardTitle>Add Mutual Fund</CardTitle>
                                    <button
                                        onClick={() => {
                                            setShowAddModal(false);
                                            setSearchResult(null);
                                            setSearchCode('');
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded-full"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </CardHeader>
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <Label>Scheme Code</Label>
                                    <div className="flex gap-2 mt-2">
                                        <Input
                                            type="text"
                                            placeholder="e.g., 120503"
                                            value={searchCode}
                                            onChange={(e) => setSearchCode(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && searchFund()}
                                        />
                                        <Button onClick={searchFund} disabled={searching}>
                                            <Search size={18} />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Find scheme codes at{' '}
                                        <a
                                            href="https://www.mfapi.in/"
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-purple-600 hover:underline"
                                        >
                                            MFAPI.in
                                        </a>
                                    </p>
                                </div>

                                {/* Search Result */}
                                {searchResult && (
                                    <div className="border rounded-lg p-4 bg-purple-50">
                                        <h3 className="font-bold text-sm mb-2">{searchResult.schemeName}</h3>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <p className="text-gray-500">Current NAV</p>
                                                <p className="font-bold text-purple-600">₹{searchResult.currentNav}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500">Date</p>
                                                <p className="font-bold">{searchResult.date}</p>
                                            </div>
                                        </div>
                                        <Button
                                            onClick={addFundToTracking}
                                            className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                                        >
                                            Add to Tracking
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default MutualFunds;
